// Documents on a recording: drop new files (saved to OneDrive / Portal Uploads) or tick ones already on the customer file.
import { useEffect, useState } from "react";
import { ExternalLink, FolderOpen, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { getPackFiles, previewPackFile } from "@/lib/api";
import { FileDrop } from "@/pages/legal/petition/petitionShared";

const fmtSize = (n) => (n == null ? "" : n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const fmtWhen = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");

export async function openMapFile(caseRow, f) {
  if (f.web_url) { window.open(f.web_url, "_blank", "noopener"); return; }
  if (!f.key) { toast.message("No link saved for this file"); return; }
  try {
    const blob = await previewPackFile(caseRow.case_id, f.key);
    window.open(URL.createObjectURL(blob), "_blank", "noopener");
  } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
}

export function MapFileChips({ caseRow, files, testid = "map-step-files" }) {
  if (!files?.length) return null;
  return (
    <ul className="flex flex-wrap gap-1" data-testid={testid}>
      {files.map((f, i) => (
        <li key={`${f.key || f.name}-${i}`}>
          <button type="button" onClick={() => openMapFile(caseRow, f)} className="inline-flex items-center gap-1 max-w-[240px] border rounded bg-white px-1.5 py-0.5 text-[10px] text-slate-700 hover:border-[#3B70E0] hover:text-[#1f4bb3]" title={`${f.origin || ""}${f.by ? ` · ${f.by}` : ""}`} data-testid={`${testid}-${i}`}>
            <Paperclip className="w-2.5 h-2.5 shrink-0" /><span className="truncate">{f.name}</span><ExternalLink className="w-2.5 h-2.5 shrink-0 opacity-60" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function MapFilesField({ caseRow, uploads, setUploads, picks, setPicks, busy }) {
  const [browse, setBrowse] = useState(false);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!browse || rows) return;
    getPackFiles(caseRow.case_id).then((r) => setRows(r.files || [])).catch((e) => { setRows([]); toast.error(e?.response?.data?.detail || e.message); });
  }, [browse, rows, caseRow.case_id]);
  const picked = (r) => picks.some((p) => p.key === r.key);
  const toggle = (r) => setPicks(picked(r) ? picks.filter((p) => p.key !== r.key) : [...picks, { source: "case_file", key: r.key, name: r.name, origin: r.origin, size: r.size }]);
  const shown = (rows || []).filter((r) => !q.trim() || `${r.name} ${r.origin}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="rounded border border-slate-200 px-2.5 py-2 space-y-1.5" data-testid="map-files">
      <div className="flex items-center gap-2 font-medium text-slate-800"><Paperclip className="w-3.5 h-3.5" /> Documents for this step
        <button type="button" onClick={() => setBrowse((v) => !v)} className="ml-auto text-[11px] font-normal text-[#1f4bb3] hover:underline inline-flex items-center gap-1" disabled={busy} data-testid="map-files-browse">
          <FolderOpen className="w-3 h-3" /> {browse ? "Hide the file's documents" : "From the file…"}
        </button>
      </div>
      <FileDrop files={uploads} setFiles={setUploads} busy={busy} hint="Drop or add files — saved to OneDrive / Portal Uploads on the customer's folder" testId="map-files-upload" label="Add file" />
      {picks.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" data-testid="map-files-picked">
          {picks.map((p, i) => (
            <li key={p.key} className="inline-flex items-center gap-1 border rounded bg-[#3B70E0]/5 border-[#3B70E0]/30 px-2 py-0.5 text-[11px] max-w-[260px]" data-testid={`map-files-pick-${i}`}>
              <span className="text-[9px] text-[#1f4bb3]">{p.origin}</span><span className="truncate">{p.name}</span>
              <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => setPicks(picks.filter((x) => x.key !== p.key))} disabled={busy} data-testid={`map-files-unpick-${i}`}><X className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
      )}
      {browse && (
        <div className="border rounded bg-white" data-testid="map-files-list">
          <div className="p-1.5 border-b"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name or source…" className="h-7 text-xs" data-testid="map-files-filter" /></div>
          {rows === null ? <div className="py-4 flex justify-center text-[11px] text-slate-500 gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading the file…</div>
            : !shown.length ? <div className="py-3 text-center text-[11px] text-slate-400 italic" data-testid="map-files-empty">{rows.length ? "Nothing matches" : "No documents on this file yet"}</div>
            : (
              <ul className="divide-y max-h-48 overflow-y-auto">
                {shown.map((r, i) => (
                  <li key={r.key}>
                    <label className="flex items-center gap-2 px-2 py-1 text-[11px] cursor-pointer hover:bg-slate-50" data-testid={`map-files-row-${i}`}>
                      <input type="checkbox" checked={picked(r)} onChange={() => toggle(r)} disabled={busy} data-testid={`map-files-row-${i}-tick`} />
                      <span className="text-[9px] px-1 rounded border bg-slate-50 text-slate-600 shrink-0">{r.origin}</span>
                      <span className="truncate flex-1">{r.name}</span>
                      <span className="text-[10px] text-slate-400 shrink-0">{fmtWhen(r.last_modified)}</span>
                      <span className="text-[10px] text-slate-400 shrink-0 w-12 text-right tabular-nums">{fmtSize(r.size)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
        </div>
      )}
    </div>
  );
}
